import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Col, Form, Switch } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import Spinner from 'common/spinner/components/spinner';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, Dropdown, Flex, FormField, notification, Row } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import {
  announcementPublishOptions,
} from '../../../utils/app-configs';
import { getFullDateFromFormValues, isObjectNullOrEmpty } from '../../../utils/helpers';
import Prompt from '../../../utils/prompt';
import {
  fetchBannerAnnouncement,
} from '../../containers/announcements/announcements-actions';
import {
  fetchStandaloneMode,
  updateStandaloneMode,
} from '../../containers/standalone-mode/standalone-mode-actions';
// eslint-disable-next-line max-len
import useConfirmationMessage from '../../../utils/hooks/useConfirmationMessage';
import PublishOptionSelector from '../announcements/publish-option-selector';
import AnnouncementsForStandalone from './announcements-for-standalone';
import { getStandaloneDefaults, getStandalonePublishDetails } from './standalone-helpers';

const Container = styled.div`
  margin: 25px 15px;
`;

const SwitchWrapper = styled(Flex)`
  gap: 15px;
`;

const StyledSwitch = styled(Switch)`
  margin: 5px 10px;
  background-color: ${p => p.checked ?
    p.theme.colors.theme :
    p.theme.colors.themeSecondary
  } !important;
`;

const Buttons = styled(Row)`
  gap: 20px;
`;

const StyledButton = styled(Button)`
  text-transform: uppercase;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const StyledRow = styled(Row)`
  margin-bottom: 30px;
`;

const ChangedBy = styled(Flex)`
  margin-left: 10px;
  gap: 3px;
  color: ${p => p.theme.colors.textLight};
  font-size: 12px;
`;

const StandaloneMode = ({
  intl,
}) => {
  const [form] = Form.useForm();
  const [isInitalized, setIsInitalized] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [subErrors, setSubErrors] = useState([]);
  const [formStartingState, setFormStartingState] = useState('');
  const isTurnedOn = Form.useWatch('isTurnedOn', form);
  const selectedPublishOption = Form.useWatch('standalonePublishOption', form);
  const dispatch = useDispatch();
  const [showConfirm] = useConfirmationMessage();
  const {
    isLoadingStandaloneMode,
    standaloneMode,
    isUpdatingStandaloneMode,
    updateStandaloneModeSuccess,
    standaloneModeError,
  } = useSelector(state => state.standaloneMode);
  const isDisabled = isUpdatingStandaloneMode || !isTurnedOn;

  const debouncedValidate = useRef(
    debounce(() => {
      // Debounce validation so we don't run validation that much on every keystroke
      // This is to check the subAnnouncements' errors so we can
      // show the error icon in the tab title
      form.validateFields([
        ['before', 'title'],
        ['before', 'message'],
        ['during', 'title'],
        ['during', 'message'],
        ['after', 'title'],
        ['after', 'message'],
      ])
        .then(() => {
          // Validation pass -> clear errrors to ultimately hide the error icon
          setSubErrors([]);
        })
        .catch((errorInfo) => {
          setSubErrors(errorInfo.errorFields);
        });
    }, 300),
  ).current;

  useEffect(() => {
    dispatch(fetchStandaloneMode());
  }, []);

  useDeepCompareEffect(() => {
    if (!isObjectNullOrEmpty(standaloneMode)) {
      const {
        publishOptionToSelect,
        startDate,
        endDate,
      } = getStandalonePublishDetails(standaloneMode);

      const standaloneDefaults = getStandaloneDefaults(standaloneMode);

      const shouldSetBeforeTemplate =
        !standaloneMode?.beforeAnnouncement?.title?.length &&
        !standaloneMode?.beforeAnnouncement?.message?.length;
      const shouldSetDuringTemplate =
        !standaloneMode?.duringAnnouncement?.title?.length &&
        !standaloneMode?.duringAnnouncement?.message?.length;
      const shouldSetAfterTemplate =
        !standaloneMode?.afterAnnouncement?.title?.length &&
        !standaloneMode?.afterAnnouncement?.message?.length;

      form.setFieldsValue({
        isTurnedOn: standaloneMode?.isTurnedOn,
        standalonePublishOption: publishOptionToSelect,
        startDate,
        endDate,
        scopes: standaloneMode?.scopes?.length ?
          standaloneMode?.scopes :
          [standaloneDefaults.scope.id],
        before: {
          template: shouldSetBeforeTemplate ? standaloneDefaults.before.template.id : null,
          title: shouldSetBeforeTemplate ?
            standaloneDefaults.before.template.title :
            standaloneMode?.beforeAnnouncement?.title,
          message: shouldSetBeforeTemplate ?
            standaloneDefaults.before.template.message :
            standaloneMode?.beforeAnnouncement?.message,
          isPinned: standaloneMode?.beforeAnnouncement?.isPinned,
          severity:
            standaloneMode?.beforeAnnouncement?.severity ||
            standaloneDefaults.before.severity.id,
          hasBanner: standaloneMode?.beforeAnnouncement?.hasBanner,
          isShown: !!standaloneMode?.beforeAnnouncement?.daysBeforeStandaloneMode,
          forDays: standaloneMode?.beforeAnnouncement?.daysBeforeStandaloneMode || 7,
        },
        during: {
          template: shouldSetDuringTemplate ? standaloneDefaults.during.template.id : null,
          title: shouldSetDuringTemplate ?
            standaloneDefaults.during.template.title :
            standaloneMode?.duringAnnouncement?.title,
          message: shouldSetDuringTemplate ?
            standaloneDefaults.during.template.message :
            standaloneMode?.duringAnnouncement?.message,
          isPinned: standaloneMode?.duringAnnouncement?.isPinned,
          severity:
            standaloneMode?.duringAnnouncement?.severity ||
            standaloneDefaults.during.severity.id,
          isShown: true,
          hasBanner: true,
        },
        after: {
          template: shouldSetAfterTemplate ? standaloneDefaults.after.template.id : null,
          title: shouldSetAfterTemplate ?
            standaloneDefaults.after.template.title :
            standaloneMode?.afterAnnouncement?.title,
          message: shouldSetAfterTemplate ?
            standaloneDefaults.after.template.message :
            standaloneMode?.afterAnnouncement?.message,
          isPinned: standaloneMode?.afterAnnouncement?.isPinned,
          severity:
            standaloneMode?.afterAnnouncement?.severity ||
            standaloneDefaults.after.severity.id,
          hasBanner: standaloneMode?.afterAnnouncement?.hasBanner,
          isShown: !!standaloneMode?.afterAnnouncement?.daysAfterStandaloneMode,
          forDays: standaloneMode?.afterAnnouncement?.daysAfterStandaloneMode || 1,
        },
      });

      setFormStartingState(JSON.stringify(form.getFieldsValue()));
      setIsInitalized(true);
    }
  }, [standaloneMode]);

  useEffect(() => {
    // If we switch to immediateIndefinite or turn off the standalone mode
    // clear the publish options (dates, datevalidators)
    // Also, need to check initalization to prevent the form being reset somehow on mount
    // resulting in the loaded dates cleared
    if (isInitalized) {
      if (selectedPublishOption === announcementPublishOptions.immediateInfinite || !isTurnedOn) {
        form.resetFields([
          'standalonePublishOption',
          ['startDate', 'date'],
          ['startDate', 'hour'],
          ['startDate', 'minute'],
          ['endDate', 'date'],
          ['endDate', 'hour'],
          ['endDate', 'minute'],
          'startDateValidator',
          'endDateValidator',
        ]);
      }
    }
  }, [selectedPublishOption, isTurnedOn]);

  useEffect(() => {
    if (updateStandaloneModeSuccess) {
      notification.success({
        message: intl.formatMessage({ id: 'message_center.SUCCESS' }),
        placement: 'top',
      });

      dispatch(fetchBannerAnnouncement());
      // Update form "default" state
      setFormStartingState(JSON.stringify(form.getFieldsValue()));
      setIsFormDirty(false);
    }
  }, [updateStandaloneModeSuccess]);

  const formValuesChanged = () => {
    // Re-validate subtab errors
    debouncedValidate();

    // Check form dirty state
    const formValuesString = JSON.stringify(form.getFieldsValue());
    if (formValuesString !== formStartingState) {
      setIsFormDirty(true);
    } else {
      setIsFormDirty(false);
    }
  };

  const handleUndo = () => {
    const formStartingParsed = JSON.parse(formStartingState);
    form.setFieldsValue({
      ...formStartingParsed,
      // We have to parse the dates back to moment objects
      startDate: {
        date: moment(formStartingParsed?.startDate?.date),
        hour: moment(formStartingParsed?.startDate?.hour),
        minute: moment(formStartingParsed?.startDate?.minute),
      },
      endDate: {
        date: moment(formStartingParsed?.endDate?.date),
        hour: moment(formStartingParsed?.endDate?.hour),
        minute: moment(formStartingParsed?.endDate?.minute),
      },
    });

    setIsFormDirty(false);
  };

  const handleSave = () => {
    const formValues = form.getFieldsValue();

    const publishOption = formValues.standalonePublishOption;
    let startDate = null;
    let endDate = null;

    if (publishOption === announcementPublishOptions.immediateInfinite) {
      startDate = null;
      endDate = null;
    } else if (publishOption === announcementPublishOptions.specific) {
      startDate = getFullDateFromFormValues(formValues.startDate).toISOString();
      endDate = getFullDateFromFormValues(formValues.endDate).toISOString();
    }

    const dataToSend = {
      isTurnedOn: formValues?.isTurnedOn,
      startDate,
      endDate,
      scopes: formValues?.scopes,
      beforeAnnouncement: {
        title: formValues?.before?.title,
        message: formValues?.before?.message,
        isPinned: formValues?.before?.isPinned,
        severity: formValues?.before?.severity,
        hasBanner: formValues?.before?.hasBanner,
        daysBeforeStandaloneMode: formValues?.before?.isShown ? formValues?.before?.forDays : 0,
      },
      duringAnnouncement: {
        title: formValues?.during?.title,
        message: formValues?.during?.message,
        isPinned: formValues?.during?.isPinned,
        hasBanner: true,
        severity: formValues?.during?.severity,
      },
      afterAnnouncement: {
        title: formValues?.after?.title,
        message: formValues?.after?.message,
        isPinned: formValues?.after?.isPinned,
        severity: formValues?.after?.severity,
        hasBanner: formValues?.after?.hasBanner,
        daysafterStandaloneMode: formValues?.after?.isShown ? formValues?.after?.forDays : 0,
      },
    };

    dispatch(updateStandaloneMode(dataToSend));
  };

  const handleFail = (errorObject) => {
    setSubErrors(errorObject.errorFields);
  };

  const handleSubmit = () => {
    form.validateFields()
      .then(() => handleSave())
      .catch(errorObject => handleFail(errorObject));
  };

  const renderLastChangedBy = () => {
    const { changerDisplayName, lastChangeDateUtc } = standaloneMode;
    if (changerDisplayName && lastChangeDateUtc) {
      return <ChangedBy>
        <span>{intl.formatMessage({ id: 'standaloneMode.LAST_CHANGED' })}</span>
        <span>{changerDisplayName.replace(',', '')}</span>
        <span>{intl.formatMessage({ id: 'standaloneMode.ON_DATE' })}</span>
        <span>{moment(lastChangeDateUtc).format('MMMM Do, YYYY')}</span>
        <span>{intl.formatMessage({ id: 'standaloneMode.AT_TIME' })}</span>
        <span>{moment(lastChangeDateUtc).format('HH:mm')}</span>
      </ChangedBy>;
    }
    return null;
  };

  return (
    <Container>
      <ErrorHandler type='notification' error={standaloneModeError} />
      <Prompt
        when={isFormDirty}
        message={intl.formatMessage({ id: 'prompt.UNSAVED_CHANGES' })}
        baseUrl='settings'
      />
      {isLoadingStandaloneMode ?
        <Spinner /> :
        <Form
          layout='horizontal'
          onFinish={handleSave}
          onFinishFailed={handleFail}
          hidden={isLoadingStandaloneMode}
          requiredMark={false}
          form={form}
          onValuesChange={formValuesChanged}
        >
          <SwitchWrapper aic marginBottom={30}>
            <Flex aic>
              <FormField
                name='isTurnedOn'
                height={null}
                valuePropName='checked'
                initialValue={false}
              >
                <StyledSwitch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  disabled={isUpdatingStandaloneMode}
                />
              </FormField>
              {`${intl.formatMessage({ id: 'standaloneMode.STANDALONE_MODE' })} ${isTurnedOn ?
                intl.formatMessage({ id: 'standaloneMode.ON' }) :
                intl.formatMessage({ id: 'standaloneMode.OFF' })
                }`}
              {renderLastChangedBy()}
            </Flex>
          </SwitchWrapper>
          <StyledRow>
            <Col span={10}>
              <FormField
                label={intl.formatMessage({
                  id: 'announcements.edit.SCOPE',
                })}
                name='scopes'
                height={null}
                initialValue={[]}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_SCOPE' }),
                }]}
              >
                <Dropdown
                  multiSelect={true}
                  values={standaloneMode?.wizardServiceCenters || []}
                  valueKey='id'
                  valueLabel='name'
                  disabled={isDisabled}
                />
              </FormField>
            </Col>
          </StyledRow>

          <StyledRow>
            <Col span={10}>
              <PublishOptionSelector
                selectedPublishOption={selectedPublishOption}
                fieldName='standalonePublishOption'
                initialValue={announcementPublishOptions.immediateInfinite}
                disabled={isDisabled}
                canDraft={false}
              />
            </Col>
          </StyledRow>

          <StyledRow>
            <Buttons marginTop={20}>
              <StyledButton disabled={!isFormDirty} onClick={() => showConfirm(handleUndo)}>
                {intl.formatMessage({ id: 'standaloneMode.UNDO_CHANGES' })}
              </StyledButton>
              <StyledButton
                onClick={handleSubmit}
                type='secondary'
                disabled={!isFormDirty}
              >
                {intl.formatMessage({
                  id: 'standaloneMode.SAVE_CHANGES',
                })}
              </StyledButton>
            </Buttons>
          </StyledRow>

          <StyledRow>
            <Col span={10}>
              <AnnouncementsForStandalone
                form={form}
                disabled={isDisabled}
                subErrors={subErrors}
              />
            </Col>
          </StyledRow>
        </Form>
      }
    </Container>
  );
};


StandaloneMode.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(StandaloneMode);
